import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from 'react-query';
import api, { getImageUrl } from '../services/api';
import { X, Search, Truck, MapPin, User, Phone, Mail, Plus, Minus, ShoppingCart, Send, Package, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { buscarTaxaPorBairro } from '../utils/bairroUtils';
import './ModalDelivery.css';

const CATEGORIA_CARNES_DO_DIA = 'CARNES DO DIA';
const CATEGORIA_CARNE_EXTRA = 'CARNES DO DIA - EXTRA';
const NOME_CARNE_EXTRA = 'Carne extra';
const PREFIXO_GUARNICAO = 'GUARNIÇÃO: ';
const PREFIXO_CARNE = 'CARNE: ';

const normalizarTexto = (v) => String(v || '').trim().toLowerCase();

const isProdutoMarmitex = (produto) => {
  const categoria = normalizarTexto(produto?.categoria);
  if (categoria === 'marmitex') return true;
  const nome = normalizarTexto(produto?.nome);
  return nome.includes('marmitex');
};

const obterLimiteCarnes = (produto, variacao) => {
  const base = normalizarTexto(variacao?.nome || produto?.nome);
  const match = base.match(/(\d+)\s*carn/);
  if (match && match[1]) {
    const n = parseInt(match[1], 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  if (base.includes('pequena')) return 1;
  if (base.includes('media') || base.includes('média')) return 2;
  if (base.includes('grande')) return 3;
  return 1;
};

const contarCarnesSelecionadas = (adicionais) => {
  const arr = Array.isArray(adicionais) ? adicionais : [];
  return arr
    .filter((a) => normalizarTexto(a?.nome).startsWith(normalizarTexto(PREFIXO_CARNE)))
    .reduce((acc, a) => acc + Math.min(20, Math.max(1, Number(a?.quantidade || 1))), 0);
};

const ModalDelivery = ({ isOpen, onClose, estabelecimento, caixaId }) => {
  const [step, setStep] = useState('cliente'); // 'cliente', 'endereco', 'produtos', 'carrinho'
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(null);
  const [buscaProduto, setBuscaProduto] = useState('');
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [modalCustomizacao, setModalCustomizacao] = useState(false);

  const [formaPagamento, setFormaPagamento] = useState('dinheiro');
  const [trocoPara, setTrocoPara] = useState('');
  const [horarioMarmita, setHorarioMarmita] = useState('');
  const [clienteEncontrado, setClienteEncontrado] = useState(null);

  // Dados do cliente
  const [cliente, setCliente] = useState({
    nome: '',
    telefone: '',
    cpf: '',
    email: ''
  });
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  
  // Endereço
  const [endereco, setEndereco] = useState({
    cep: '',
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: estabelecimento?.endereco?.cidade || 'Cachoeiro de Itapemirim',
    estado: estabelecimento?.endereco?.estado || 'ES',
    referencia: ''
  });
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  const [bairroSelecionado, setBairroSelecionado] = useState('');
  
  // Carrinho
  const [carrinho, setCarrinho] = useState([]);
  
  // Customização do produto
  const [customizacaoAtual, setCustomizacaoAtual] = useState({
    produto: null,
    variacao: null,
    adicionais: [],
    marmitexSemCarne: false,
    marmitexPermitirExtra: false,
    observacoes: '',
    quantidade: 1
  });

  // Buscar produtos
  const { data: produtos, isLoading: loadingProdutos } = useQuery(
    ['produtos-delivery', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const params = new URLSearchParams();
      params.append('estabelecimento', estabelecimento._id);
      params.append('disponivel', 'true');
      const response = await api.get(`/api/produtos?${params}`);
      return response.data.data.produtos || [];
    },
    {
      enabled: !!estabelecimento?._id && isOpen
    }
  );

  // Buscar adicionais globais (caso os produtos não tenham adicionais embutidos)
  const { data: adicionaisGlobais } = useQuery(
    ['adicionais-globais-delivery', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const response = await api.get(`/api/adicionais?estabelecimento=${estabelecimento._id}&disponivel=true`);
      return response.data.data.adicionais || [];
    },
    {
      enabled: !!estabelecimento?._id && isOpen,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );

  // Agrupar produtos por categoria
  const produtosPorCategoria = useMemo(() => {
    if (!produtos || produtos.length === 0) return {};
    const agrupados = {};
    produtos.forEach(produto => {
      const categoria = produto.categoria || 'Outros';
      if (!agrupados[categoria]) {
        agrupados[categoria] = [];
      }
      agrupados[categoria].push(produto);
    });
    return agrupados;
  }, [produtos]);

  // Filtrar produtos por busca
  const produtosFiltrados = useMemo(() => {
    if (!categoriaSelecionada) return [];
    const produtosCategoria = produtosPorCategoria[categoriaSelecionada] || [];
    if (!buscaProduto.trim()) return produtosCategoria;
    const buscaLower = buscaProduto.toLowerCase();
    return produtosCategoria.filter(p => 
      p.nome.toLowerCase().includes(buscaLower) ||
      (p.descricao && p.descricao.toLowerCase().includes(buscaLower))
    );
  }, [categoriaSelecionada, produtosPorCategoria, buscaProduto]);

  // Bairros cadastrados
  const bairrosCadastrados = useMemo(() => {
    if (!estabelecimento?.configuracoes?.taxasPorBairro) return [];
    return estabelecimento.configuracoes.taxasPorBairro.map(b => ({
      nome: b.bairro,
      taxa: b.taxa
    })).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [estabelecimento]);

  // Calcular taxa de entrega
  const taxaEntrega = useMemo(() => {
    if (!endereco.bairro || !estabelecimento?.configuracoes) return 0;
    const taxaEntregaAtiva = estabelecimento?.configuracoes?.taxaEntregaAtiva !== false;
    if (!taxaEntregaAtiva) return 0;
    const taxasPorBairro = estabelecimento.configuracoes.taxasPorBairro || [];
    const taxaPadrao = estabelecimento.configuracoes.taxaEntrega || 0;
    const resultado = buscarTaxaPorBairro(endereco.bairro, taxasPorBairro, taxaPadrao);
    return resultado.taxa;
  }, [endereco.bairro, estabelecimento]);

  // Buscar cliente por telefone
  const buscarClientePorTelefone = async (telefone) => {
    if (!telefone || !estabelecimento?._id) return;
    
    const telefoneLimpo = telefone.replace(/\D/g, '');
    // Aceitar apenas 11 dígitos (com DDD)
    if (telefoneLimpo.length !== 11) {
      return;
    }
    
    setBuscandoCliente(true);
    try {
      
      const response = await api.get(`/api/clientes/buscar/${telefoneLimpo}?estabelecimento=${estabelecimento._id}`, {
        validateStatus: (status) => status === 200 || status === 404
      });
      
      if (response.status === 200 && response.data.success && response.data.data.cliente) {
        const clienteEncontrado = response.data.data.cliente;
        setClienteEncontrado(clienteEncontrado);
        setCliente({
          nome: clienteEncontrado.nome || '',
          telefone: clienteEncontrado.telefone || telefone,
          cpf: clienteEncontrado.cpf || '',
          email: clienteEncontrado.email || ''
        });
        
        // Preencher endereço se o cliente tiver endereços cadastrados
        if (clienteEncontrado.enderecos && clienteEncontrado.enderecos.length > 0) {
          const ultimoEndereco = clienteEncontrado.enderecos[clienteEncontrado.enderecos.length - 1];
          setEndereco(prev => ({
            ...prev,
            cep: ultimoEndereco.cep || prev.cep,
            rua: ultimoEndereco.rua || prev.rua,
            numero: ultimoEndereco.numero || prev.numero,
            complemento: ultimoEndereco.complemento || prev.complemento,
            bairro: ultimoEndereco.bairro || prev.bairro,
            cidade: ultimoEndereco.cidade || prev.cidade,
            estado: ultimoEndereco.estado || prev.estado,
            referencia: ultimoEndereco.referencia || prev.referencia
          }));
          
          // Se tiver bairro, verificar se está na lista de bairros cadastrados
          if (ultimoEndereco.bairro && bairrosCadastrados.length > 0) {
            const bairroEncontrado = bairrosCadastrados.find(b => 
              b.nome.toLowerCase() === ultimoEndereco.bairro.toLowerCase()
            );
            if (bairroEncontrado) {
              setBairroSelecionado(bairroEncontrado.nome);
            }
          }
        }
        
        toast.success(`✅ Cliente encontrado: ${clienteEncontrado.nome}`);
      }
    } catch (error) {
      // Cliente não encontrado - comportamento esperado
    } finally {
      setBuscandoCliente(false);
    }
  };

  const carrinhoAceitaCrediario = useMemo(() => {
    const arr = Array.isArray(carrinho) ? carrinho : [];
    if (arr.length === 0) return false;
    return arr.every((item) => {
      const produto = item?.produto || item;
      return produto?.aceitaCrediario === true;
    });
  }, [carrinho]);

  const crediarioDisponivel = useMemo(() => {
    if (!clienteEncontrado) return false;
    if (clienteEncontrado.crediarioHabilitado !== true) return false;
    if (clienteEncontrado.crediarioBloqueado === true) return false;
    if (!clienteEncontrado.crediarioDataVencimento) return false;
    if (!carrinhoAceitaCrediario) return false;
    return true;
  }, [clienteEncontrado, carrinhoAceitaCrediario]);

  useEffect(() => {
    if (formaPagamento === 'fiado' && !crediarioDisponivel) {
      setFormaPagamento('dinheiro');
    }
  }, [formaPagamento, crediarioDisponivel]);

  // Buscar CEP
  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;

    setBuscandoCEP(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado. Preencha manualmente.');
        return;
      }

      setEndereco(prev => ({
        ...prev,
        cep: cepLimpo,
        rua: data.logradouro || prev.rua,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado
      }));

      // Se o bairro encontrado estiver na lista, selecionar automaticamente
      if (data.bairro) {
        const bairroEncontrado = bairrosCadastrados.find(b => 
          b.nome.toUpperCase() === data.bairro.toUpperCase()
        );
        if (bairroEncontrado) {
          setBairroSelecionado(bairroEncontrado.nome);
          setEndereco(prev => ({ ...prev, bairro: bairroEncontrado.nome }));
        } else {
          setEndereco(prev => ({ ...prev, bairro: data.bairro }));
        }
      }
      
      toast.success('Endereço encontrado!');
    } catch (error) {
      toast.error('Erro ao buscar CEP. Preencha manualmente.');
    } finally {
      setBuscandoCEP(false);
    }
  };

  // Abrir modal de customização
  const abrirCustomizacao = (produto) => {
    setProdutoSelecionado(produto);
    setCustomizacaoAtual({
      produto,
      variacao: produto.temVariacoes && produto.variacoes?.length > 0 ? produto.variacoes[0] : null,
      adicionais: [],
      marmitexSemCarne: false,
      marmitexPermitirExtra: false,
      observacoes: '',
      quantidade: 1
    });
    setModalCustomizacao(true);
  };

  const atualizarQuantidadeAdicional = (adicional, novaQuantidade) => {
    const qtd = Math.min(20, Math.max(1, Number(novaQuantidade || 1)));

    const isMarmitex = isProdutoMarmitex(customizacaoAtual.produto);
    const nomeAdicional = String(adicional?._id || adicional?.nome || '');
    const isCarne = normalizarTexto(adicional?.nome).startsWith(normalizarTexto(PREFIXO_CARNE));

    if (isMarmitex && isCarne && !customizacaoAtual.marmitexSemCarne) {
      const limite = obterLimiteCarnes(customizacaoAtual.produto, customizacaoAtual.variacao);
      const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
        return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
      });

      const adicionaisAtuais = Array.isArray(customizacaoAtual.adicionais) ? customizacaoAtual.adicionais : [];
      const selecionada = adicionaisAtuais.find((a) => {
        if (adicional?._id && a?._id) return String(a._id) === String(adicional._id);
        return normalizarTexto(a?.nome) === normalizarTexto(adicional?.nome);
      });
      const qtdAtual = Math.min(20, Math.max(1, Number(selecionada?.quantidade || 1)));

      const totalCarnesAgora = contarCarnesSelecionadas(adicionaisAtuais);
      const totalSemEste = totalCarnesAgora - qtdAtual;
      const novoTotal = totalSemEste + qtd;

      if (novoTotal > limite && !customizacaoAtual.marmitexPermitirExtra) {
        toast.error(`Limite de carnes: ${limite}. Marque a opção de carne extra para aumentar a quantidade.`);
        return;
      }

      if (novoTotal > limite && (!carneExtraCfg || carneExtraCfg.disponivel === false)) {
        toast.error('Carne extra não configurada/indisponível. Ative e defina o preço antes de aumentar a quantidade acima do limite.');
        return;
      }
    }

    if (isMarmitex && isCarne && customizacaoAtual.marmitexSemCarne) {
      return;
    }

    setCustomizacaoAtual(prev => ({
      ...prev,
      adicionais: (prev.adicionais || []).map(a => {
        if (adicional?._id && a?._id) return a._id === adicional._id ? { ...a, quantidade: qtd } : a;
        return a?.nome === adicional?.nome ? { ...a, quantidade: qtd } : a;
      })
    }));
  };

  // Adicionar ao carrinho
  const adicionarAoCarrinho = () => {
    if (!customizacaoAtual.produto) return;

    const isMarmitex = isProdutoMarmitex(customizacaoAtual.produto);
    const precoBase = customizacaoAtual.variacao?.preco || 
                     customizacaoAtual.produto.precoPromocional || 
                     customizacaoAtual.produto.preco;

    const adicionaisOriginais = Array.isArray(customizacaoAtual.adicionais) ? customizacaoAtual.adicionais : [];
    const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
      return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
    });

    const adicionaisSemCarneExtra = adicionaisOriginais.filter((a) => normalizarTexto(a?.nome) !== normalizarTexto(NOME_CARNE_EXTRA));
    let adicionaisFinal = adicionaisSemCarneExtra;

    if (isMarmitex) {
      const limite = obterLimiteCarnes(customizacaoAtual.produto, customizacaoAtual.variacao);
      const totalCarnes = customizacaoAtual.marmitexSemCarne ? 0 : contarCarnesSelecionadas(adicionaisSemCarneExtra);
      const excedente = Math.max(0, totalCarnes - limite);

      if (excedente > 0 && !customizacaoAtual.marmitexPermitirExtra) {
        toast.error(`Limite de carnes: ${limite}. Marque a opção de carne extra para adicionar mais.`);
        return;
      }

      if (excedente > 0 && (!carneExtraCfg || carneExtraCfg.disponivel === false)) {
        toast.error('Carne extra não configurada/indisponível. Ative e defina o preço antes de adicionar carnes acima do limite.');
        return;
      }

      if (customizacaoAtual.marmitexSemCarne) {
        adicionaisFinal = adicionaisFinal.filter((a) => !normalizarTexto(a?.nome).startsWith(normalizarTexto(PREFIXO_CARNE)));
      }

      if (excedente > 0 && carneExtraCfg && carneExtraCfg.disponivel !== false) {
        adicionaisFinal = [
          ...adicionaisFinal,
          { nome: NOME_CARNE_EXTRA, preco: Number(carneExtraCfg.preco) || 0, quantidade: excedente }
        ];
      }
    }

    const precoAdicionais = adicionaisFinal.reduce((acc, add) => {
      const qtd = Math.min(20, Math.max(1, Number(add?.quantidade || 1)));
      return acc + ((add.preco || 0) * qtd);
    }, 0);
    const precoUnitario = precoBase + precoAdicionais;

    const item = {
      produto: customizacaoAtual.produto,
      quantidade: customizacaoAtual.quantidade,
      precoUnitario,
      variacao: customizacaoAtual.variacao,
      adicionais: adicionaisFinal,
      observacoes: customizacaoAtual.observacoes,
      subtotal: precoUnitario * customizacaoAtual.quantidade
    };

    setCarrinho([...carrinho, item]);
    setModalCustomizacao(false);
    setCustomizacaoAtual({
      produto: null,
      variacao: null,
      adicionais: [],
      marmitexSemCarne: false,
      marmitexPermitirExtra: false,
      observacoes: '',
      quantidade: 1
    });
    toast.success(`${customizacaoAtual.produto.nome} adicionado ao carrinho!`);
  };

  // Remover do carrinho
  const removerDoCarrinho = (index) => {
    setCarrinho(carrinho.filter((_, i) => i !== index));
  };

  // Calcular totais
  const subtotal = carrinho.reduce((acc, item) => acc + item.subtotal, 0);
  const total = subtotal + taxaEntrega;

  const temMarmitexNoCarrinho = useMemo(() => {
    const arr = Array.isArray(carrinho) ? carrinho : [];
    return arr.some((item) => isProdutoMarmitex(item?.produto));
  }, [carrinho]);

  const opcoesHorarioMarmita = useMemo(() => {
    const inicio = String(estabelecimento?.configuracoes?.marmitaHorarioInicio || '10:00');
    const fim = String(estabelecimento?.configuracoes?.marmitaHorarioFim || '14:30');
    const intervalo = Number(estabelecimento?.configuracoes?.marmitaIntervaloMin) || 30;

    const parse = (s) => {
      const [hh, mm] = String(s || '').split(':');
      const h = Number(hh);
      const m = Number(mm);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return (h * 60) + m;
    };

    const iniMin = parse(inicio);
    const fimMin = parse(fim);
    const stepMin = Math.max(5, Math.min(180, Number.isFinite(intervalo) ? intervalo : 30));
    if (iniMin == null || fimMin == null || fimMin < iniMin) return [];

    const out = [];
    for (let t = iniMin; t <= fimMin; t += stepMin) {
      const h = String(Math.floor(t / 60)).padStart(2, '0');
      const m = String(t % 60).padStart(2, '0');
      out.push(`${h}:${m}`);
    }
    return out;
  }, [estabelecimento]);

  const valorRecebido = useMemo(() => {
    if (formaPagamento !== 'dinheiro') return total;
    const v = parseFloat(String(trocoPara).replace(',', '.'));
    return Number.isFinite(v) ? v : total;
  }, [formaPagamento, trocoPara, total]);
  const troco = useMemo(() => {
    if (formaPagamento !== 'dinheiro') return 0;
    return Math.max(0, valorRecebido - total);
  }, [formaPagamento, valorRecebido, total]);

  // Criar pedido
  const criarPedidoMutation = useMutation(
    async (dados) => {
      const response = await api.post('/api/caixa/delivery', dados);

      if (!response?.data || typeof response.data !== 'object') {
        throw new Error('Resposta inválida do servidor ao criar pedido');
      }

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao criar pedido');
      }

      return response;
    },
    {
      onSuccess: () => {
        toast.success('Pedido enviado para produção!');
        // Limpar tudo
        setCarrinho([]);
        setHorarioMarmita('');
        setCliente({ nome: '', telefone: '', cpf: '', email: '' });
        setEndereco({
          cep: '',
          rua: '',
          numero: '',
          complemento: '',
          bairro: '',
          cidade: estabelecimento?.endereco?.cidade || 'Cachoeiro de Itapemirim',
          estado: estabelecimento?.endereco?.estado || 'ES',
          referencia: ''
        });
        setBairroSelecionado('');
        setStep('cliente');
        setCategoriaSelecionada(null);
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || error.message || 'Erro ao criar pedido');
      }
    }
  );

  // Enviar para produção
  const enviarParaProducao = () => {
    // Validações
    if (!cliente.nome || !cliente.telefone) {
      toast.error('Preencha nome e telefone do cliente');
      setStep('cliente');
      return;

    }

    if (!endereco.rua || !endereco.numero || !endereco.bairro) {
      toast.error('Preencha os dados de endereço');
      setStep('endereco');
      return;
    }

    if (carrinho.length === 0) {
      toast.error('Adicione produtos ao carrinho');
      setStep('produtos');
      return;
    }

    if (!formaPagamento) {
      toast.error('Selecione a forma de pagamento');
      return;
    }

    if (formaPagamento === 'dinheiro' && valorRecebido < total) {
      toast.error('Valor recebido insuficiente');
      return;
    }

    if (!caixaId) {
      toast.error('Caixa não encontrado');
      return;
    }

    // Preparar dados
    const itens = carrinho.map(item => {
      const itemData = {
        produto: item.produto._id,
        quantidade: item.quantidade,
        precoUnitario: item.precoUnitario,
        observacoes: item.observacoes || ''
      };
      
      // Adicionar variação se existir
      if (item.variacao && item.variacao.nome) {
        itemData.variacao = {
          nome: item.variacao.nome,
          preco: item.variacao.preco
        };
      }
      
      // Adicionar acréscimos apenas se houver
      if (item.adicionais && item.adicionais.length > 0) {
        itemData.acrescimos = item.adicionais.map(add => ({
          nome: add.nome || add,
          preco: add.preco || 0,
          quantidade: Math.min(20, Math.max(1, Number(add?.quantidade || 1)))
        }));
      }
      
      return itemData;
    });

    const dadosPedido = {
      caixa: caixaId,
      estabelecimento: estabelecimento._id,
      cliente,
      enderecoEntrega: endereco,
      itens,
      horarioMarmita: (temMarmitexNoCarrinho && horarioMarmita) ? horarioMarmita : undefined,
      formaPagamento,
      dataVencimentoFiado: formaPagamento === 'fiado' ? (clienteEncontrado?.crediarioDataVencimento || undefined) : undefined,
      valorRecebido,
      troco,
      subtotal,
      taxaEntrega,
      total
    };

    criarPedidoMutation.mutate(dadosPedido);
  };

  // Resetar ao fechar
  useEffect(() => {
    if (!isOpen) {
      setStep('cliente');
      setCategoriaSelecionada(null);
      setCarrinho([]);
      setFormaPagamento('dinheiro');
      setTrocoPara('');
      setHorarioMarmita('');
      setCliente({ nome: '', telefone: '', cpf: '', email: '' });
      setEndereco({
        cep: '',
        rua: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: estabelecimento?.endereco?.cidade || 'Cachoeiro de Itapemirim',
        estado: estabelecimento?.endereco?.estado || 'ES',
        referencia: ''
      });
      setBairroSelecionado('');
      setBuscaProduto('');
      setModalCustomizacao(false);
    }
  }, [isOpen, estabelecimento]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-delivery" onClick={(e) => e.stopPropagation()}>
        <div className="modal-delivery-header">
          <h2>
            <Truck size={24} />
            Novo Pedido Delivery
          </h2>
          <button className="btn-close-modal" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Steps */}
        <div className="delivery-steps">
          <div className={`step ${step === 'cliente' ? 'active' : step === 'endereco' || step === 'produtos' || step === 'carrinho' ? 'completed' : ''}`}>
            <User size={16} />
            Cliente
          </div>
          <div className={`step ${step === 'endereco' ? 'active' : step === 'produtos' || step === 'carrinho' ? 'completed' : ''}`}>
            <MapPin size={16} />
            Endereço
          </div>
          <div className={`step ${step === 'produtos' ? 'active' : step === 'carrinho' ? 'completed' : ''}`}>
            <Package size={16} />
            Produtos
          </div>
          <div className={`step ${step === 'carrinho' ? 'active' : ''}`}>
            <ShoppingCart size={16} />
            Carrinho
          </div>
        </div>

        {/* Conteúdo do Step */}
        {step === 'cliente' && (
          <div className="delivery-step-content">
            <h3>Dados do Cliente</h3>
            <div className="form-group">
              <label>
                <Phone size={16} />
                Telefone <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={cliente.telefone}
                onChange={(e) => {
                  const telefone = e.target.value;
                  setCliente({ ...cliente, telefone });
                  const telefoneLimpo = telefone.replace(/\D/g, '');
                  if (telefoneLimpo.length === 11) {
                    buscarClientePorTelefone(telefone);
                  }
                }}
                placeholder="(00) 00000-0000"
                disabled={buscandoCliente}
              />
              {buscandoCliente && <small>Buscando cliente...</small>}
            </div>
            <div className="form-group">
              <label>
                <User size={16} />
                Nome <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={cliente.nome}
                onChange={(e) => setCliente({ ...cliente, nome: e.target.value })}
                placeholder="Nome completo"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>CPF</label>
                <input
                  type="text"
                  value={cliente.cpf}
                  onChange={(e) => setCliente({ ...cliente, cpf: e.target.value })}
                  placeholder="000.000.000-00"
                />
              </div>
              <div className="form-group">
                <label>
                  <Mail size={16} />
                  Email
                </label>
                <input
                  type="email"
                  value={cliente.email}
                  onChange={(e) => setCliente({ ...cliente, email: e.target.value })}
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (!cliente.nome || !cliente.telefone) {
                    toast.error('Preencha nome e telefone');
                    return;
                  }
                  setStep('endereco');
                }}
              >
                Próximo
              </button>
            </div>
          </div>
        )}

        {step === 'endereco' && (
          <div className="delivery-step-content">
            <h3>Endereço de Entrega</h3>
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>CEP</label>
                <input
                  type="text"
                  value={endereco.cep}
                  onChange={(e) => setEndereco({ ...endereco, cep: e.target.value })}
                  placeholder="00000-000"
                  maxLength={9}
                />
              </div>
              <button
                className="btn btn-secondary"
                onClick={() => buscarCEP(endereco.cep)}
                disabled={buscandoCEP || endereco.cep.length < 8}
                style={{ marginTop: '1.75rem' }}
              >
                {buscandoCEP ? 'Buscando...' : 'Buscar CEP'}
              </button>
            </div>
            <div className="form-group">
              <label>Bairro <span style={{ color: '#ef4444' }}>*</span></label>
              <select
                value={bairroSelecionado}
                onChange={(e) => {
                  const bairro = e.target.value;
                  setBairroSelecionado(bairro);
                  if (bairro) {
                    setEndereco(prev => ({ ...prev, bairro }));
                  }
                }}
              >
                <option value="">Selecione ou digite abaixo</option>
                {bairrosCadastrados.map((bairro, idx) => (
                  <option key={idx} value={bairro.nome}>
                    {bairro.nome} - R$ {bairro.taxa.toFixed(2)}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={endereco.bairro}
                onChange={(e) => {
                  const novoBairro = e.target.value;
                  setEndereco(prev => ({ ...prev, bairro: novoBairro }));
                  // Se o bairro digitado corresponde a um cadastrado, selecionar no dropdown
                  const bairroEncontrado = bairrosCadastrados.find(b => 
                    b.nome.toLowerCase() === novoBairro.toLowerCase()
                  );
                  if (bairroEncontrado) {
                    setBairroSelecionado(bairroEncontrado.nome);
                  } else if (!novoBairro) {
                    setBairroSelecionado('');
                  }
                }}
                placeholder="Ou digite o bairro manualmente"
                style={{ marginTop: '0.5rem' }}
              />
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 3 }}>
                <label>Rua <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={endereco.rua}
                  onChange={(e) => setEndereco({ ...endereco, rua: e.target.value })}
                  placeholder="Nome da rua"
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Número <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={endereco.numero}
                  onChange={(e) => setEndereco({ ...endereco, numero: e.target.value })}
                  placeholder="123"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Complemento</label>
              <input
                type="text"
                value={endereco.complemento}
                onChange={(e) => setEndereco({ ...endereco, complemento: e.target.value })}
                placeholder="Apto, bloco, etc"
              />
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>Cidade</label>
                <input
                  type="text"
                  value={endereco.cidade}
                  onChange={(e) => setEndereco({ ...endereco, cidade: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Estado</label>
                <input
                  type="text"
                  value={endereco.estado}
                  onChange={(e) => setEndereco({ ...endereco, estado: e.target.value })}
                  maxLength={2}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Referência</label>
              <input
                type="text"
                value={endereco.referencia}
                onChange={(e) => setEndereco({ ...endereco, referencia: e.target.value })}
                placeholder="Ponto de referência"
              />
            </div>
            {taxaEntrega > 0 && (
              <div className="taxa-info">
                <strong>Taxa de entrega: R$ {taxaEntrega.toFixed(2)}</strong>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setStep('cliente')}>
                Voltar
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  console.log('🔍 Validando endereço:', {
                    rua: endereco.rua,
                    numero: endereco.numero,
                    bairro: endereco.bairro,
                    bairroSelecionado: bairroSelecionado
                  });
                  
                  // Validar campos obrigatórios
                  if (!endereco.rua || !endereco.rua.trim()) {
                    toast.error('Preencha a rua');
                    return;
                  }
                  
                  if (!endereco.numero || !endereco.numero.trim()) {
                    toast.error('Preencha o número');
                    return;
                  }
                  
                  if (!endereco.bairro || !endereco.bairro.trim()) {
                    toast.error('Preencha o bairro');
                    return;
                  }
                  
                  console.log('✅ Validação passou, indo para produtos');
                  setStep('produtos');
                }}
              >
                Próximo
              </button>
            </div>
          </div>
        )}

        {step === 'produtos' && (
          <div className="delivery-step-content">
            {!categoriaSelecionada ? (
              <>
                <h3>Selecione uma Categoria</h3>
                <div className="categorias-grid-delivery">
                  {Object.keys(produtosPorCategoria).map(categoria => (
                    <div
                      key={categoria}
                      className="categoria-card-delivery"
                      onClick={() => setCategoriaSelecionada(categoria)}
                    >
                      <Package size={32} />
                      <h4>{categoria}</h4>
                      <p>{produtosPorCategoria[categoria].length} produto(s)</p>
                    </div>
                  ))}
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setStep('endereco')}>
                    Voltar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setStep('carrinho')}
                    disabled={carrinho.length === 0}
                  >
                    Ir para o carrinho{carrinho.length > 0 ? ` (${carrinho.length})` : ''}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="produtos-header-delivery">
                  <button
                    className="btn-voltar-categoria"
                    onClick={() => {
                      setCategoriaSelecionada(null);
                      setBuscaProduto('');
                    }}
                  >
                    ← Voltar
                  </button>
                  <h3>{categoriaSelecionada}</h3>
                  <div className="search-produtos-delivery">
                    <Search size={18} />
                    <input
                      type="text"
                      placeholder="Buscar produto..."
                      value={buscaProduto}
                      onChange={(e) => setBuscaProduto(e.target.value)}
                    />
                  </div>
                </div>
                {loadingProdutos ? (
                  <div className="loading-produtos">Carregando produtos...</div>
                ) : produtosFiltrados.length === 0 ? (
                  <div className="empty-produtos">Nenhum produto encontrado</div>
                ) : (
                  <div className="produtos-grid-delivery">
                    {produtosFiltrados.map(produto => (
                      <div
                        key={produto._id}
                        className="produto-card-delivery"
                        onClick={() => abrirCustomizacao(produto)}
                      >
                        {produto.imagens?.[0]?.url && (
                          <div className="produto-image-delivery">
                            <img src={getImageUrl(produto.imagens[0].url)} alt={produto.nome} />
                          </div>
                        )}
                        <div className="produto-info-delivery">
                          <h4>{produto.nome}</h4>
                          <p className="produto-preco-delivery">
                            R$ {(produto.precoPromocional || produto.preco).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {carrinho.length > 0 && (
                  <div 
                    className="carrinho-flutuante"
                    style={{
                      background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                      backgroundColor: '#FF6B35'
                    }}
                  >
                    <ShoppingCart size={20} />
                    <span>{carrinho.length} item(s)</span>
                    <button
                      className="btn-ver-carrinho"
                      onClick={() => setStep('carrinho')}
                    >
                      Ver Carrinho
                    </button>
                  </div>
                )}
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => {
                    setCategoriaSelecionada(null);
                    setBuscaProduto('');
                  }}>
                    Voltar para Categorias
                  </button>
                  <button className="btn btn-secondary" onClick={() => {
                    setCategoriaSelecionada(null);
                    setBuscaProduto('');
                    setStep('endereco');
                  }}>
                    Voltar para Endereço
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setStep('carrinho')}
                    disabled={carrinho.length === 0}
                  >
                    Ir para o carrinho{carrinho.length > 0 ? ` (${carrinho.length})` : ''}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {step === 'carrinho' && (
          <div className="delivery-step-content">
            <h3>Carrinho</h3>
            {carrinho.length === 0 ? (
              <div className="empty-carrinho">
                <ShoppingCart size={48} />
                <p>Carrinho vazio</p>
                <button className="btn btn-primary" onClick={() => setStep('produtos')}>
                  Adicionar Produtos
                </button>
              </div>
            ) : (
              <>
                <div className="carrinho-items-delivery">
                  {carrinho.map((item, index) => (
                    <div key={index} className="carrinho-item-delivery">
                      <div className="item-info-delivery">
                        <h4>{item.produto.nome}</h4>
                        {item.variacao && <p className="variacao-info">Variação: {item.variacao.nome}</p>}
                        {item.adicionais.length > 0 && (
                          <p className="adicionais-info">
                            {(() => {
                              const isMarmitexItem = isProdutoMarmitex(item.produto);
                              const adicionais = Array.isArray(item.adicionais) ? item.adicionais : [];
                              if (!isMarmitexItem) {
                                return `Adicionais: ${adicionais
                                  .map((a) => `${Math.min(20, Math.max(1, Number(a?.quantidade || 1)))}x ${a.nome}${a.preco > 0 ? ` (+R$ ${Number(a.preco).toFixed(2)})` : ''}`)
                                  .join(', ')}`;
                              }

                              const guarnicoes = [];
                              const carnes = [];
                              const extras = [];

                              adicionais.forEach((a) => {
                                const nome = String(a?.nome || '');
                                const nomeNorm = normalizarTexto(nome);
                                const qtd = Math.min(20, Math.max(1, Number(a?.quantidade || 1)));
                                const preco = Number(a?.preco) || 0;

                                if (nomeNorm === normalizarTexto(NOME_CARNE_EXTRA)) {
                                  extras.push(`${qtd}x ${NOME_CARNE_EXTRA}${preco > 0 ? ` (+R$ ${preco.toFixed(2)})` : ''}`);
                                  return;
                                }

                                if (nomeNorm.startsWith(normalizarTexto(PREFIXO_GUARNICAO))) {
                                  guarnicoes.push(`${qtd}x ${nome.replace(PREFIXO_GUARNICAO, '')}${preco > 0 ? ` (+R$ ${preco.toFixed(2)})` : ''}`);
                                  return;
                                }

                                if (nomeNorm.startsWith(normalizarTexto(PREFIXO_CARNE))) {
                                  carnes.push(`${qtd}x ${nome.replace(PREFIXO_CARNE, '')}`);
                                  return;
                                }

                                extras.push(`${qtd}x ${nome}${preco > 0 ? ` (+R$ ${preco.toFixed(2)})` : ''}`);
                              });

                              const partes = [];
                              if (guarnicoes.length > 0) partes.push(`Guarnições: ${guarnicoes.join(', ')}`);
                              if (carnes.length > 0) partes.push(`Carnes: ${carnes.join(', ')}`);
                              if (extras.length > 0) partes.push(extras.join(', '));
                              return `Adicionais: ${partes.join(' | ')}`;
                            })()}
                          </p>
                        )}
                        {item.observacoes && <p className="observacoes-info">📝 {item.observacoes}</p>}
                        <p className="quantidade-info">Quantidade: {item.quantidade}x</p>
                      </div>
                      <div className="item-actions-delivery">
                        <p className="item-preco-delivery">R$ {item.subtotal.toFixed(2)}</p>
                        <button
                          className="btn-remove-item"
                          onClick={() => removerDoCarrinho(index)}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="carrinho-totais-delivery">
                  <div className="total-line">
                    <span>Subtotal:</span>
                    <strong>R$ {subtotal.toFixed(2)}</strong>
                  </div>
                  <div className="total-line">
                    <span>Taxa de entrega:</span>
                    <strong>R$ {taxaEntrega.toFixed(2)}</strong>
                  </div>
                  <div className="total-line total-final">
                    <span>Total:</span>
                    <strong>R$ {total.toFixed(2)}</strong>
                  </div>
                </div>

                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                  {temMarmitexNoCarrinho && (
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                      <label>Horário da Marmita</label>
                      {opcoesHorarioMarmita.length > 0 ? (
                        <select value={horarioMarmita} onChange={(e) => setHorarioMarmita(e.target.value)}>
                          <option value="">Selecione</option>
                          {opcoesHorarioMarmita.map((h) => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      ) : (
                        <input type="time" value={horarioMarmita} onChange={(e) => setHorarioMarmita(e.target.value)} />
                      )}
                    </div>
                  )}

                  <div className="form-group">
                    <label>Forma de Pagamento</label>
                    <select value={formaPagamento} onChange={(e) => setFormaPagamento(e.target.value)}>
                      <option value="dinheiro">💵 Dinheiro</option>
                      <option value="pix">📱 PIX</option>
                      <option value="cartao_credito">💳 Cartão de Crédito</option>
                      <option value="cartao_debito">💳 Cartão de Débito</option>
                      <option value="vale_refeicao">🍽️ Vale Refeição</option>
                      {crediarioDisponivel && <option value="fiado">🧾 Crediário</option>}
                    </select>
                  </div>

                  {formaPagamento === 'dinheiro' && (
                    <>
                      <div className="form-group">
                        <label>Troco para quanto?</label>
                        <input
                          type="number"
                          step="0.01"
                          min={total}
                          value={trocoPara}
                          onChange={(e) => setTrocoPara(e.target.value)}
                          placeholder={total.toFixed(2)}
                        />
                      </div>
                      {troco > 0 && (
                        <div className="taxa-info">
                          <strong>Troco: R$ {troco.toFixed(2)}</strong>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setStep('produtos')}>
                    Voltar
                  </button>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button className="btn btn-secondary" onClick={() => setStep('produtos')}>
                      Adicionar Mais
                    </button>
                    <button
                      className="btn btn-primary btn-enviar-producao"
                      onClick={enviarParaProducao}
                      disabled={criarPedidoMutation.isLoading}
                    >
                      <Send size={20} />
                      {criarPedidoMutation.isLoading ? 'Enviando...' : 'ENVIAR P/ PRODUÇÃO'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Modal de Customização */}
        {modalCustomizacao && produtoSelecionado && (
          <div className="modal-overlay-inner" onClick={() => setModalCustomizacao(false)}>
            <div className="modal-content modal-customizacao" onClick={(e) => e.stopPropagation()}>
              <div className="modal-customizacao-header">
                <h3>{produtoSelecionado.nome}</h3>
                <button onClick={() => setModalCustomizacao(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="customizacao-content">
                {produtoSelecionado.temVariacoes && produtoSelecionado.variacoes?.length > 0 && (
                  <div className="customizacao-section">
                    <label>Variação</label>
                    <div className="variacoes-grid">
                      {produtoSelecionado.variacoes.map((variacao, idx) => (
                        <button
                          key={idx}
                          className={`variacao-btn ${customizacaoAtual.variacao?.nome === variacao.nome ? 'active' : ''}`}
                          onClick={() => setCustomizacaoAtual({ ...customizacaoAtual, variacao })}
                        >
                          <span>{variacao.nome}</span>
                          <span>R$ {variacao.preco.toFixed(2)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isProdutoMarmitex(produtoSelecionado) && (
                  <>
                    {produtoSelecionado.adicionais && produtoSelecionado.adicionais.length > 0 && (
                      <div className="customizacao-section">
                        <label>Guarnições</label>
                        <div className="adicionais-list">
                          {produtoSelecionado.adicionais
                            .filter((g) => g && g.disponivel !== false)
                            .map((guarnicao, idx) => {
                              const nomeMarcado = `${PREFIXO_GUARNICAO}${guarnicao.nome}`;
                              const checked = customizacaoAtual.adicionais.some(a => normalizarTexto(a?.nome) === normalizarTexto(nomeMarcado));
                              return (
                                <div key={idx} className="adicional-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setCustomizacaoAtual({
                                          ...customizacaoAtual,
                                          adicionais: [...customizacaoAtual.adicionais, { nome: nomeMarcado, preco: Number(guarnicao.preco) || 0, quantidade: 1 }]
                                        });
                                      } else {
                                        setCustomizacaoAtual({
                                          ...customizacaoAtual,
                                          adicionais: customizacaoAtual.adicionais.filter(a => normalizarTexto(a?.nome) !== normalizarTexto(nomeMarcado))
                                        });
                                      }
                                    }}
                                  />
                                  <span>{guarnicao.nome}</span>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    <div className="customizacao-section">
                      <label>Carnes do Dia</label>
                      <div className="adicionais-list">
                        {(() => {
                          const limite = obterLimiteCarnes(produtoSelecionado, customizacaoAtual.variacao);
                          const totalCarnes = customizacaoAtual.marmitexSemCarne ? 0 : contarCarnesSelecionadas(customizacaoAtual.adicionais);

                          return (
                            <>
                              <div style={{ marginTop: '0.25rem', fontSize: '0.9rem', opacity: 0.85 }}>
                                Carnes: {totalCarnes}/{limite}
                              </div>
                            </>
                          );
                        })()}

                        {(Array.isArray(adicionaisGlobais) ? adicionaisGlobais : [])
                          .filter((a) => a && a.disponivel !== false && normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNES_DO_DIA))
                          .sort((a, b) => (Number(a?.ordem) || 0) - (Number(b?.ordem) || 0) || String(a?.nome || '').localeCompare(String(b?.nome || '')))
                          .map((carne) => {
                            const nomeMarcado = `${PREFIXO_CARNE}${carne.nome}`;
                            const selecionada = customizacaoAtual.adicionais.find((a) => normalizarTexto(a?.nome) === normalizarTexto(nomeMarcado));
                            const checked = Boolean(selecionada);
                            const qtdAtual = Math.min(20, Math.max(1, Number(selecionada?.quantidade || 1)));

                            return (
                              <div key={carne._id} className="adicional-checkbox">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={customizacaoAtual.marmitexSemCarne}
                                  onChange={(e) => {
                                    const limite = obterLimiteCarnes(produtoSelecionado, customizacaoAtual.variacao);
                                    const totalCarnes = customizacaoAtual.marmitexSemCarne ? 0 : contarCarnesSelecionadas(customizacaoAtual.adicionais);

                                    if (e.target.checked) {
                                      const novoTotal = totalCarnes + 1;
                                      if (novoTotal > limite && !customizacaoAtual.marmitexPermitirExtra) {
                                        toast.error(`Limite de carnes: ${limite}. Marque a opção de carne extra para adicionar mais.`);
                                        return;
                                      }
                                      if (novoTotal > limite) {
                                        const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
                                          return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
                                        });
                                        if (!carneExtraCfg || carneExtraCfg.disponivel === false) {
                                          toast.error('Carne extra não configurada/indisponível.');
                                          return;
                                        }
                                      }
                                      setCustomizacaoAtual({
                                        ...customizacaoAtual,
                                        adicionais: [...customizacaoAtual.adicionais, { nome: nomeMarcado, preco: 0, quantidade: 1 }]
                                      });
                                    } else {
                                      setCustomizacaoAtual({
                                        ...customizacaoAtual,
                                        adicionais: customizacaoAtual.adicionais.filter((a) => normalizarTexto(a?.nome) !== normalizarTexto(nomeMarcado))
                                      });
                                    }
                                  }}
                                />
                                <span>{carne.nome}</span>
                                {checked && !customizacaoAtual.marmitexSemCarne && (
                                  <input
                                    type="number"
                                    min={1}
                                    max={20}
                                    value={qtdAtual}
                                    onChange={(e) => {
                                      const limite = obterLimiteCarnes(produtoSelecionado, customizacaoAtual.variacao);
                                      const qtdNova = Math.min(20, Math.max(1, Number(e.target.value || 1)));
                                      const totalCarnes = contarCarnesSelecionadas(customizacaoAtual.adicionais);
                                      const totalSemEste = totalCarnes - qtdAtual;
                                      const novoTotal = totalSemEste + qtdNova;
                                      if (novoTotal > limite && !customizacaoAtual.marmitexPermitirExtra) {
                                        toast.error(`Limite de carnes: ${limite}. Marque a opção de carne extra para aumentar a quantidade.`);
                                        return;
                                      }
                                      if (novoTotal > limite) {
                                        const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
                                          return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
                                        });
                                        if (!carneExtraCfg || carneExtraCfg.disponivel === false) {
                                          toast.error('Carne extra não configurada/indisponível.');
                                          return;
                                        }
                                      }
                                      atualizarQuantidadeAdicional({ nome: nomeMarcado }, qtdNova);
                                    }}
                                    style={{ width: '70px', marginLeft: '0.5rem' }}
                                  />
                                )}
                              </div>
                            );
                          })}

                        {(() => {
                          const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
                            return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
                          });
                          const precoExtra = Number(carneExtraCfg?.preco) || 0;

                          return (
                            <>
                              <div className="adicional-checkbox" style={{ marginTop: '0.75rem' }}>
                                <input
                                  type="checkbox"
                                  checked={customizacaoAtual.marmitexPermitirExtra}
                                  disabled={customizacaoAtual.marmitexSemCarne || !carneExtraCfg || carneExtraCfg.disponivel === false}
                                  onChange={(e) => {
                                    if (customizacaoAtual.marmitexSemCarne) return;
                                    setCustomizacaoAtual({ ...customizacaoAtual, marmitexPermitirExtra: e.target.checked });
                                  }}
                                />
                                <span>
                                  Adicionar carne extra{carneExtraCfg ? ` (+ R$ ${precoExtra.toFixed(2)} cada)` : ''}
                                </span>
                              </div>

                              <div className="adicional-checkbox">
                                <input
                                  type="checkbox"
                                  checked={customizacaoAtual.marmitexSemCarne}
                                  onChange={(e) => {
                                    const semCarne = e.target.checked;
                                    setCustomizacaoAtual({
                                      ...customizacaoAtual,
                                      marmitexSemCarne: semCarne,
                                      marmitexPermitirExtra: semCarne ? false : customizacaoAtual.marmitexPermitirExtra,
                                      adicionais: semCarne
                                        ? customizacaoAtual.adicionais.filter((a) => !normalizarTexto(a?.nome).startsWith(normalizarTexto(PREFIXO_CARNE)))
                                        : customizacaoAtual.adicionais
                                    });
                                  }}
                                />
                                <span>Sem carne</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </>
                )}

                {!isProdutoMarmitex(produtoSelecionado) && produtoSelecionado.adicionais && produtoSelecionado.adicionais.length > 0 && (
                  <div className="customizacao-section">
                    <label>Adicionais</label>
                    <div className="adicionais-list">
                      {produtoSelecionado.adicionais.map((adicional, idx) => (
                        <div key={idx} className="adicional-checkbox">
                          <input
                            type="checkbox"
                            checked={customizacaoAtual.adicionais.some(a => a.nome === adicional.nome)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCustomizacaoAtual({
                                  ...customizacaoAtual,
                                  adicionais: [...customizacaoAtual.adicionais, { ...adicional, quantidade: 1 }]
                                });
                              } else {
                                setCustomizacaoAtual({
                                  ...customizacaoAtual,
                                  adicionais: customizacaoAtual.adicionais.filter(a => a.nome !== adicional.nome)
                                });
                              }
                            }}
                          />
                          <span>{adicional.nome}</span>
                          {adicional.preco > 0 && <span>+ R$ {adicional.preco.toFixed(2)}</span>}
                          {customizacaoAtual.adicionais.some(a => a.nome === adicional.nome) && (
                            <input
                              type="number"
                              min={1}
                              max={20}
                              value={customizacaoAtual.adicionais.find(a => a.nome === adicional.nome)?.quantidade || 1}
                              onChange={(e) => atualizarQuantidadeAdicional(adicional, e.target.value)}
                              style={{ width: '70px', marginLeft: '0.5rem' }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!isProdutoMarmitex(produtoSelecionado) && (!produtoSelecionado.adicionais || produtoSelecionado.adicionais.length === 0) && adicionaisGlobais && adicionaisGlobais.length > 0 && (
                  <div className="customizacao-section">
                    <label>Adicionais</label>
                    <div className="adicionais-list">
                      {adicionaisGlobais.map((adicional, idx) => (
                        <div key={adicional._id || idx} className="adicional-checkbox">
                          <input
                            type="checkbox"
                            checked={customizacaoAtual.adicionais.some(a => a._id === adicional._id || a.nome === adicional.nome)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCustomizacaoAtual({
                                  ...customizacaoAtual,
                                  adicionais: [...customizacaoAtual.adicionais, { ...adicional, quantidade: 1 }]
                                });
                              } else {
                                setCustomizacaoAtual({
                                  ...customizacaoAtual,
                                  adicionais: customizacaoAtual.adicionais.filter(a => (a._id || a.nome) !== (adicional._id || adicional.nome))
                                });
                              }
                            }}
                          />
                          <span>{adicional.nome}</span>
                          {adicional.preco > 0 && <span>+ R$ {adicional.preco.toFixed(2)}</span>}
                          {customizacaoAtual.adicionais.some(a => (a._id || a.nome) === (adicional._id || adicional.nome)) && (
                            <input
                              type="number"
                              min={1}
                              max={20}
                              value={customizacaoAtual.adicionais.find(a => (a._id || a.nome) === (adicional._id || adicional.nome))?.quantidade || 1}
                              onChange={(e) => atualizarQuantidadeAdicional(adicional, e.target.value)}
                              style={{ width: '70px', marginLeft: '0.5rem' }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="customizacao-section">
                  <label>Observações</label>
                  <textarea
                    value={customizacaoAtual.observacoes}
                    onChange={(e) => setCustomizacaoAtual({ ...customizacaoAtual, observacoes: e.target.value })}
                    placeholder="Ex: Sem cebola, bem passado..."
                    rows={3}
                  />
                </div>
                <div className="customizacao-section">
                  <label>Quantidade</label>
                  <div className="quantidade-controls">
                    <button
                      className="btn-quantidade"
                      onClick={() => {
                        if (customizacaoAtual.quantidade > 1) {
                          setCustomizacaoAtual({ ...customizacaoAtual, quantidade: customizacaoAtual.quantidade - 1 });
                        }
                      }}
                    >
                      <Minus size={18} />
                    </button>
                    <span className="quantidade-value">{customizacaoAtual.quantidade}</span>
                    <button
                      className="btn-quantidade"
                      onClick={() => {
                        setCustomizacaoAtual({ ...customizacaoAtual, quantidade: customizacaoAtual.quantidade + 1 });
                      }}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setModalCustomizacao(false)}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={adicionarAoCarrinho}>
                    <Plus size={18} />
                    Adicionar ao Carrinho
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalDelivery;

